<div class="nf-panel cov-dialog">
    <div class="nf-panel__header nf-panel__header--action">
        <div class="nf-panel__heading">
            <div class="nf-panel__title">Coverage</div>
            <!-- The title + the colored graph carry the coverage story on the microsegment view (showTimeRange),
                 so the subtitle only adds the headline % + window in the create flow, where traffic is selected. -->
            @if (!data.showTimeRange) {
            <div class="nf-panel__subtitle">
                @if (trafficPercent !== null) { {{ trafficPercent }}% of selected traffic covered }
                @else { Coverage of the selected traffic }
                @if (windowLabel) { <span class="cov-window">&middot; {{ windowLabel }}</span> }
            </div>
            }
        </div>
        <button type="button" class="cov-close" aria-label="Close" (click)="close()">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
    </div>

    <div class="cov-toolbar">
        @if (hasWindow) {
        <div class="cov-seg" role="tablist" aria-label="Graph scope">
            <button type="button" role="tab" [attr.aria-selected]="mode === 'selected'"
                [class.cov-on]="mode === 'selected'" (click)="setMode('selected')">Selected only</button>
            <button type="button" role="tab" [attr.aria-selected]="mode === 'full'"
                [class.cov-on]="mode === 'full'" (click)="setMode('full')">Full graph</button>
        </div>
        }

        @if (data.showTimeRange) {
        <div class="cov-rangebar">
            <app-time-range-pill [selected]="rangeFilter" [ranges]="rangeOptions"
                (rangeChange)="onRangePreset($event)" (pillReclick)="onRangeReclick($event)"></app-time-range-pill>
            <div class="cov-daterange">
                <button type="button" class="cov-daterange__box" [class.is-active]="rangeFilter === 'custom'"
                    [class.is-open]="showCustom" (click)="toggleCustomRange()" aria-label="Custom date range">
                    <span class="pi pi-calendar cov-daterange__icon" aria-hidden="true"></span>
                    <span class="cov-daterange__text">{{ rangeReadout }}</span>
                </button>
                @if (showCustom) {
                <div class="cov-daterange__scrim" (click)="showCustom = false" aria-hidden="true"></div>
                <div class="cov-daterange__pop">
                    <app-date-range-calendar [start]="data.start ?? null" [end]="data.end ?? null"
                        [maxDays]="rangeMaxDays" (apply)="onCustomRange($event)"
                        (cancel)="showCustom = false"></app-date-range-calendar>
                </div>
                }
            </div>
        </div>
        }

    </div>

    <div class="nf-panel__body cov-body">
        @if (degraded) {
        <div class="cov-note">Showing a graph built from the selection only. Open this from Traffic Analysis
            with its time window to see the full traffic context.</div>
        }
        @if (error) {
        <div class="cov-note cov-note--warn">{{ error }}</div>
        }

        @if (loading && nodes.length === 0) {
        <div class="cov-stage">
            <div class="cov-chart cov-chart--empty">
                <div class="cov-empty">
                    <span class="cov-spinner" aria-hidden="true"></span>
                    <p class="cov-empty__msg">Loading traffic&hellip;</p>
                </div>
            </div>
        </div>
        } @else if (!loading && !hasAnyFlows) {
        <div class="cov-stage">
            <div class="cov-chart cov-chart--empty">
                <div class="cov-empty">
                    <svg class="cov-empty__icon" viewBox="0 0 48 32" fill="none" aria-hidden="true">
                        <rect x="2" y="4" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <rect x="2" y="19" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <rect x="41" y="4" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <rect x="41" y="19" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <path d="M7 8.5C20 8.5 28 23.5 41 23.5" stroke="currentColor" stroke-width="3" opacity="0.3" />
                        <path d="M7 23.5C20 23.5 28 8.5 41 8.5" stroke="currentColor" stroke-width="3" opacity="0.3" />
                    </svg>
                    <p class="cov-empty__msg">No traffic to show for this selection.</p>
                </div>
            </div>
        </div>
        } @else {
        <div class="cov-stage">
            <div class="cov-chart">
                <!-- Coverage color key AND additive visibility toggle in one, floating in the canvas
                     top-left corner over the chart: each swatch toggles that state's flows in the graph on
                     or off (independent, all on by default), so it doubles as the legend. -->
                <div class="cov-flowkey" role="group" aria-label="Coverage visibility">
                    <button type="button" class="cov-key" [class.cov-key--off]="!visibleStates.has('covered')"
                        [attr.aria-pressed]="visibleStates.has('covered')" (click)="toggleState('covered')">
                        <i class="sw sw-covered"></i>Covered
                    </button>
                    <button type="button" class="cov-key" [class.cov-key--off]="!visibleStates.has('partial')"
                        [attr.aria-pressed]="visibleStates.has('partial')" (click)="toggleState('partial')">
                        <i class="sw sw-partial"></i>Partial
                    </button>
                    <button type="button" class="cov-key" [class.cov-key--off]="!visibleStates.has('uncovered')"
                        [attr.aria-pressed]="visibleStates.has('uncovered')" (click)="toggleState('uncovered')">
                        <i class="sw sw-uncovered"></i>Uncovered
                    </button>
                    @if (mode === 'full') {
                    <button type="button" class="cov-key" [class.cov-key--off]="!showNotSelected"
                        [attr.aria-pressed]="showNotSelected" (click)="toggleNotSelected()">
                        <i class="sw sw-notsel"></i>Not selected
                    </button>
                    }
                </div>
                <!-- The legend stays above; when its toggles have hidden every flow, show a message in the
                     canvas rather than tearing the chart (and the legend) out - so the toggles stay reachable. -->
                @if (isEmpty) {
                <div class="cov-empty">
                    <svg class="cov-empty__icon" viewBox="0 0 48 32" fill="none" aria-hidden="true">
                        <rect x="2" y="4" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <rect x="2" y="19" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <rect x="41" y="4" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <rect x="41" y="19" width="5" height="9" rx="1.5" fill="currentColor" opacity="0.55" />
                        <path d="M7 8.5C20 8.5 28 23.5 41 23.5" stroke="currentColor" stroke-width="3" opacity="0.3" />
                        <path d="M7 23.5C20 23.5 28 8.5 41 8.5" stroke="currentColor" stroke-width="3" opacity="0.3" />
                    </svg>
                    <p class="cov-empty__msg">No flows match the shown coverage states.</p>
                </div>
                } @else {
                <app-flow-map-chart [nodes]="nodes" [links]="links" [headers]="headers"
                    [highlightNodeIds]="highlightNodeIds" [highlightLinkIds]="highlightLinkIds"
                    ariaLabel="Service coverage flow map"
                    (nodeHover)="onNodeHover($event)"
                    (nodeClick)="onNodeClick($event)"
                    (linkClick)="onLinkClick($event)"
                    (nodeToggle)="onNodeToggle($event)"
                    (backgroundClick)="onBackground()"></app-flow-map-chart>
                }
                <!-- Re-query (time range / scope change): keep the current chart in place and overlay a
                     spinner instead of tearing it out, so the modal doesn't collapse and re-expand. -->
                @if (loading) {
                <div class="cov-chart__loading" aria-live="polite" aria-label="Updating">
                    <span class="cov-spinner" aria-hidden="true"></span>
                </div>
                }
            </div>
            @if (detail && !isEmpty) {
            <app-detail-drawer [eyebrow]="detail.eyebrow" [title]="detail.title" (closed)="closeDetail()">
                <app-coverage-detail-content [readOnly]="data.showTimeRange" [explain]="detail.explain"
                    (rowHover)="onDetailRowHover($event)"
                    (toggleInclude)="onDetailIncludeToggle($event)"></app-coverage-detail-content>
            </app-detail-drawer>
            }
        </div>
        }
    </div>
</div>
